import { Link, useLocation } from '@tanstack/react-router';
import { ChevronUp, Radio } from 'lucide-react';

import { useSidebarState } from '../../../shared/hooks/use-sidebar-state';

import { Button } from '@/shared/components/shadcn/button';
import {
  SidebarGroupContent,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/shared/components/shadcn/sidebar';
import { useSavedQueries } from '@/shared/hooks/use-saved-queries';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils/cn';
import { MenuItem } from './sidebar-item';

export const SavedQueriesMenuItem = () => {
  const { workspaceId } = useWorkspaceId();
  const { isSavedMenuExpanded, toggleSavedMenu } = useSidebarState();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-x-0.5">
        <MenuItem route="/workspace/$workspaceId/listening" name="Listening" Icon={Radio} />
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-sidebar-accent shrink-0 p-2"
          onClick={toggleSavedMenu}
          aria-expanded={isSavedMenuExpanded}
          aria-controls="saved-queries-list"
          aria-label={isSavedMenuExpanded ? 'Collapse saved queries' : 'Expand saved queries'}
        >
          <ChevronUp
            className={cn(
              'size-5 transition-transform',
              isSavedMenuExpanded ? 'rotate-180' : 'rotate-0',
            )}
          />
        </Button>
      </div>

      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isSavedMenuExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <SidebarGroupContent
            id="saved-queries-list"
            className={cn(
              'flex-1 overflow-y-auto py-0.5 transition-all duration-200',
              isSavedMenuExpanded ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
            )}
          >
            <SavedQueriesList workspaceId={workspaceId} />
          </SidebarGroupContent>
        </div>
      </div>
    </div>
  );
};

export const SavedQueriesList = ({ workspaceId }: { workspaceId: string }) => {
  const { data: savedQueries } = useSavedQueries({ workspaceId });
  const location = useLocation();

  // Extract query id from search params
  const searchParams = new URLSearchParams(location.search);
  const activeQueryId = searchParams.get('q');

  if (!savedQueries || savedQueries.length === 0) {
    return (
      <div className="border-sidebar-border mx-3.5 translate-x-px border-l px-2.5 py-2">
        <div className="text-muted-foreground text-sm">No saved queries yet</div>
      </div>
    );
  }

  return (
    <SidebarMenuSub>
      {savedQueries.map((query) => {
        const isActive = activeQueryId === query.id;
        return (
          <SidebarMenuSubItem key={query.id}>
            <SidebarMenuSubButton asChild isActive={isActive}>
              <Link
                to="/workspace/$workspaceId/insight"
                params={{ workspaceId }}
                search={{ q: query.id }}
              >
                <span className="text-sidebar-foreground truncate">{query.name}</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
};
